import React, { useState } from 'react'
import { observer } from 'mobx-react'
import { Redirect, Route, RouteChildrenProps } from 'react-router'
import { useHoverState } from 'hooks/hover-notify'
import {
  SplitView,
  SplitViewSide,
  SplitViewMain,
  SplitViewToggleButton,
} from 'components/SplitView'
import AdminNav from '../../components/AdminNav'
import InventoryManager from '../../inventory/views/InventoryManager'
import PayoutsAdminRoot from 'payouts/admin/views/PayoutsAdminRoot'
import AdminViewModel from 'admin/AdminViewModel'
import ChangeRequests from 'admin/scope-change-requests/views/ScopeChangeRequests'
import Icon from 'components/Icon'
import styles from './Admin.sass'
import BundleManagerPage from 'admin/bundle/views/BundleManager/BundleManagerPage'
import { getFeatureToggleClient } from 'infra/featureToggles'
import Staffing from '../../staffing/components/Staffing'

interface AdminProps extends RouteChildrenProps {
  vm: AdminViewModel
}

export default observer(function Admin({ match, vm }: AdminProps) {
  const [highlightBorder, setHighlightBorder] = useState(false)

  const toggleRef = React.useRef<HTMLAnchorElement | null>(null)
  const isToggleHovering = useHoverState(toggleRef, setHighlightBorder)

  const toggleClient = getFeatureToggleClient()
  const showBundleManagerPage = toggleClient.variation(
    'WorkPortal.ShowBundleManagerPage',
    false
  )

  return (
    <SplitView>
      <SplitViewSide
        collapsed={!vm.navVisible}
        highlightBorder={highlightBorder}
      >
        <AdminNav vm={vm} />
      </SplitViewSide>
      <SplitViewMain>
        <div className={styles.toggleButtonContainer}>
          <SplitViewToggleButtonContainer
            onAdminNavSidebarToggleClick={vm.toggleNavVisibility}
            isAdminNavSidebarVisible={vm.navVisible}
            isToggleHovering={isToggleHovering}
          />
        </div>
        <Route path={`${match?.url}`} exact>
          <Redirect push={false} to={`${match?.url}/change-requests`} />
        </Route>
        {vm.canManageJobScopeChangeRequest && (
          <Route path={`${match?.url}/change-requests`}>
            <ChangeRequests vm={vm.scopeChangeRequestsVM} />
          </Route>
        )}
        {vm.canManageServices && (
          <Route path={`${match?.url}/service-manager`}>
            <InventoryManager vm={vm.inventoryManagerVm} />
          </Route>
        )}
        {vm.canManageServices && showBundleManagerPage && (
          <Route path={`${match?.url}/bundle-manager`}>
            <BundleManagerPage vm={vm.bundleManagerVm} />
          </Route>
        )}
        {vm.canSeeStaffingManager && vm.staffingVm && (
          <Route path={`${match?.url}/staffing-manager`}>
            <Staffing vm={vm.staffingVm} />
          </Route>
        )}
        {vm.isPlatformAdmin && vm.payoutsAdminVm && (
          <Route path={`${match?.url}/payouts`}>
            <PayoutsAdminRoot vm={vm.payoutsAdminVm} />
          </Route>
        )}
      </SplitViewMain>
    </SplitView>
  )
})

interface SplitViewToggleButtonContainerProps {
  onAdminNavSidebarToggleClick?: () => unknown
  isAdminNavSidebarVisible: boolean
  isToggleHovering: boolean
  toggleRef?: React.Ref<HTMLAnchorElement>
}

export const SplitViewToggleButtonContainer = observer(
  ({
    onAdminNavSidebarToggleClick,
    isAdminNavSidebarVisible,
    isToggleHovering,
    toggleRef,
  }: SplitViewToggleButtonContainerProps) => {
    if (onAdminNavSidebarToggleClick) {
      return (
        <SplitViewToggleButton
          onClick={onAdminNavSidebarToggleClick}
          ref={toggleRef}
        >
          {isToggleHovering ? (
            isAdminNavSidebarVisible ? (
              <Icon name="hover-arrow-highlight" />
            ) : (
              <Icon name="hover-menu-highlight" />
            )
          ) : isAdminNavSidebarVisible ? (
            <Icon name="hover-arrow" />
          ) : (
            <Icon name="hover-menu" />
          )}
        </SplitViewToggleButton>
      )
    }
    return null
  }
)
